import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  createTrainingJob, 
  getTrainingJobs, 
  getAllModels, 
  deployModel,
  getPipelines,
  updatePipelineStatus 
} from '@/api/modelStudioApi';
import { getServices, getApiUrl } from '@/api/api';
import { ragApi, INCIDENT_CATEGORIES, CATEGORY_LABELS } from '@/api/incidentApi';
import { Brain, Play, CheckCircle, XCircle, Clock, TrendingUp, Zap, Rocket, Database, Bot, Upload, FileText, BookOpen, Cloud, BarChart3 } from 'lucide-react';
import DocumentManager from './DocumentManager';
import ExternalRAGAgentForm from './ExternalRAGAgentForm';
import ExternalRAGStats from './ExternalRAGStats';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const ModelStudio = () => {
  const [trainingJobs, setTrainingJobs] = useState([]);
  const [models, setModels] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [services, setServices] = useState([]);
  const [ragAgents, setRAGAgents] = useState([]);
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTrainingDialog, setShowTrainingDialog] = useState(false);
  const [showDeployDialog, setShowDeployDialog] = useState(false);
  const [showRAGAgentDialog, setShowRAGAgentDialog] = useState(false);
  const [showKnowledgeBaseDialog, setShowKnowledgeBaseDialog] = useState(false);
  const [showDocumentManager, setShowDocumentManager] = useState(false);
  const [showExternalRAGForm, setShowExternalRAGForm] = useState(false);
  const [showExternalRAGStats, setShowExternalRAGStats] = useState(false);
  const [selectedKnowledgeBase, setSelectedKnowledgeBase] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [activeTab, setActiveTab] = useState('models');
  
  // Training form state
  const [trainingForm, setTrainingForm] = useState({
    serviceName: '',
    metricName: 'avg_response_time',
    ensembleMethod: 'lscp',
    epochs: 50,
    trainingDataPoints: 200,
  });
  
  // Deploy form state
  const [deployForm, setDeployForm] = useState({
    versionId: '',
    pipelineName: '',
  });

  // RAG Agent form state
  const [ragAgentForm, setRAGAgentForm] = useState({
    name: '',
    description: '',
    categories: [],
    knowledge_bases: [],
    model_provider: 'openai',
    model_name: 'gpt-4o',
    system_prompt: 'You are an expert IT incident response specialist. Analyze incidents and provide structured, actionable recommendations based on established SOPs and knowledge base information.',
    max_tokens: 2000,
    temperature: 0.1,
    confidence_threshold: 0.7,
    auto_assign: false,
    learning_enabled: true
  });

  // Detail view states  
  const [selectedRAGAgent, setSelectedRAGAgent] = useState(null);
  const [showKnowledgeBaseDetail, setShowKnowledgeBaseDetail] = useState(false);
  const [showRAGAgentDetail, setShowRAGAgentDetail] = useState(false);
  const [showAddProcedure, setShowAddProcedure] = useState(false);
  const [agentStats, setAgentStats] = useState({});
  const [editingKB, setEditingKB] = useState(false);
  const [editingAgent, setEditingAgent] = useState(false);
  const [testingLLM, setTestingLLM] = useState(false);
  const [testPrompt, setTestPrompt] = useState('');
  const [testResult, setTestResult] = useState('');
  
  // New procedure form
  const [newProcedure, setNewProcedure] = useState({
    step: 1,
    title: '',
    actions: ['']
  });

  // Knowledge Base form state  
  const [knowledgeBaseForm, setKnowledgeBaseForm] = useState({
    name: '',
    description: '',
    category: '',
    content_type: 'structured',
    structured_content: {},
    tags: [],
    created_by: 'admin@junohawkai.com'
  });

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5000); // Refresh every 5 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [jobsData, modelsData, pipelinesData, servicesData, ragAgentsData, knowledgeBasesData] = await Promise.all([
        getTrainingJobs(),
        getAllModels(),
        getPipelines(),
        getServices(),
        ragApi.getRAGAgents().catch(() => []),
        ragApi.getKnowledgeBases().catch(() => [])
      ]);
      
      setTrainingJobs(jobsData);
      setModels(modelsData);
      setPipelines(pipelinesData);
      setServices(servicesData);
      setRAGAgents(ragAgentsData);
      setKnowledgeBases(knowledgeBasesData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching model studio data:', error);
      setLoading(false);
    }
  };

  const handleCreateTrainingJob = async () => {
    try {
      await createTrainingJob(
        trainingForm.serviceName,
        trainingForm.metricName,
        trainingForm.ensembleMethod,
        trainingForm.epochs,
        trainingForm.trainingDataPoints
      );
      setShowTrainingDialog(false);
      fetchData();
    } catch (error) {
      console.error('Error creating training job:', error);
    }
  };

  const handleDeployModel = async () => {
    try {
      await deployModel(deployForm.versionId, deployForm.pipelineName);
      setShowDeployDialog(false);
      fetchData();
    } catch (error) {
      console.error('Error deploying model:', error);
    }
  };

  const handleTogglePipeline = async (pipelineId, currentStatus) => {
    try {
      await updatePipelineStatus(pipelineId, !currentStatus);
      fetchData();
    } catch (error) {
      console.error('Error toggling pipeline:', error);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'training':
        return <Play className="w-5 h-5 text-blue-500 animate-spin" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'training':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const metrics = ['avg_response_time', 'request_count', 'error_rate', 'cpu_usage', 'memory_usage'];
  const ensembleMethods = [
    { value: 'lscp', label: 'LSCP (F1: 0.86)' },
    { value: 'isolation_forest', label: 'Isolation Forest (F1: 0.84)' },
    { value: 'ocsvm', label: 'One-Class SVM (F1: 0.82)' },
  ];

  // RAG Agent functions
  const handleCreateRAGAgent = async () => {
    try {
      await ragApi.createRAGAgent(ragAgentForm);
      setShowRAGAgentDialog(false);
      setRAGAgentForm({
        name: '',
        description: '',
        categories: [],
        knowledge_bases: [],
        model_provider: 'openai',
        model_name: 'gpt-4o',
        system_prompt: 'You are an expert IT incident response specialist. Analyze incidents and provide structured, actionable recommendations based on established SOPs and knowledge base information.',
        max_tokens: 2000,
        temperature: 0.1,
        confidence_threshold: 0.7,
        auto_assign: false,
        learning_enabled: true
      });
      fetchData();
    } catch (error) {
      console.error('Error creating RAG agent:', error);
    }
  };

  const handleCreateKnowledgeBase = async () => {
    try {
      await ragApi.createKnowledgeBase(knowledgeBaseForm);
      setShowKnowledgeBaseDialog(false);
      setKnowledgeBaseForm({
        name: '',
        description: '',
        category: '',
        content_type: 'structured',
        structured_content: {},
        tags: [],
        created_by: 'admin@junohawkai.com'
      });
      fetchData();
    } catch (error) {
      console.error('Error creating knowledge base:', error);
    }
  };

  const handleCreateMockRAGData = async () => {
    try {
      await Promise.all([
        ragApi.createMockKnowledgeBases(),
        ragApi.createMockRAGAgents()
      ]);
      fetchData();
    } catch (error) {
      console.error('Error creating mock RAG data:', error);
    }
  };

  // Knowledge Base detail functions
  const handleViewKnowledgeBase = async (kb) => {
    try {
      console.log('Clicking on Knowledge Base:', kb.name, kb.id);
      setSelectedKnowledgeBase(kb); // Set the basic KB first
      setShowDocumentManager(true); // Show document manager
      
      // Then fetch detailed information
      const details = await ragApi.getKnowledgeBaseDetails(kb.id);
      console.log('KB Details received:', details);
      setSelectedKnowledgeBase(details);
    } catch (error) {
      console.error('Error fetching knowledge base details:', error);
      // Still show the modal with basic information
      setSelectedKnowledgeBase(kb);
      setShowKnowledgeBaseDetail(true);
    }
  };

  const handleUpdateKnowledgeBase = async (updates) => {
    try {
      await ragApi.updateKnowledgeBase(selectedKnowledgeBase.id, updates);
      // Refresh knowledge base details
      const updatedDetails = await ragApi.getKnowledgeBaseDetails(selectedKnowledgeBase.id);
      setSelectedKnowledgeBase(updatedDetails);
      // Refresh the list
      fetchData();
    } catch (error) {
      console.error('Error updating knowledge base:', error);
    }
  };

  const handleAddProcedure = async () => {
    try {
      await ragApi.addProcedureToKnowledgeBase(selectedKnowledgeBase.id, newProcedure);
      setNewProcedure({ step: 1, title: '', actions: [''] });
      setShowAddProcedure(false);
      // Refresh the knowledge base details
      const updatedDetails = await ragApi.getKnowledgeBaseDetails(selectedKnowledgeBase.id);
      setSelectedKnowledgeBase(updatedDetails);
    } catch (error) {
      console.error('Error adding procedure:', error);
    }
  };

  // RAG Agent detail functions
  const handleViewRAGAgent = async (agent) => {
    try {
      console.log('Clicking on RAG Agent:', agent.name, agent.id);
      setSelectedRAGAgent(agent); // Set the basic agent first
      setShowRAGAgentDetail(true); // Show modal immediately
      
      // Then fetch detailed statistics
      const details = await ragApi.getRAGAgentDetails(agent.id);
      console.log('Agent Details received:', details);
      setSelectedRAGAgent(details);
      setAgentStats(details.statistics || {});
    } catch (error) {
      console.error('Error fetching RAG agent details:', error);
      // Still show the modal with basic information
      setSelectedRAGAgent(agent);
      setAgentStats({
        incidents_processed: agent.incidents_processed || 0,
        success_rate: agent.success_rate || 0,
        total_recommendations: 0,
        learning_events: 0
      });
      setShowRAGAgentDetail(true);
    }
  };

  const handleUpdateRAGAgent = async (updates) => {
    try {
      await ragApi.updateRAGAgent(selectedRAGAgent.id, updates);
      // Refresh agent details
      const updatedDetails = await ragApi.getRAGAgentDetails(selectedRAGAgent.id);
      setSelectedRAGAgent(updatedDetails);
      fetchData(); // Refresh the list
    } catch (error) {
      console.error('Error updating RAG agent:', error);
    }
  };

  const handleTestLLM = async () => {
    if (!testPrompt.trim()) return;
    
    setTestingLLM(true);
    try {
      // For now, simulate a test - in real implementation you'd call an endpoint
      const testResponse = `**Test Response from ${selectedRAGAgent.model_provider}/${selectedRAGAgent.model_name}:**

Based on your prompt: "${testPrompt}"

This is a simulated response showing how the LLM would handle this input with current settings:
- Temperature: ${selectedRAGAgent.temperature}
- Max Tokens: ${selectedRAGAgent.max_tokens}
- Model: ${selectedRAGAgent.model_provider}/${selectedRAGAgent.model_name}

The response demonstrates the agent's reasoning capabilities and adherence to the system prompt configuration.`;

      setTestResult(testResponse);
    } catch (error) {
      console.error('Error testing LLM:', error);
      setTestResult('Error: Failed to test LLM. Please check configuration and try again.');
    } finally {
      setTestingLLM(false);
    }
  };

  // External RAG Agent handlers
  const handleCreateExternalRAGAgent = async (agentData) => {
    try {
      const response = await fetch(getApiUrl('/api/external-rag-agents'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(agentData)
      });

      if (!response.ok) {
        throw new Error('Failed to create external RAG agent');
      }

      const result = await response.json();
      setShowExternalRAGForm(false);
      
      // Show success message or redirect to stats page
      alert('External RAG agent created successfully!');
      setShowExternalRAGStats(true);
      
    } catch (error) {
      console.error('Error creating external RAG agent:', error);
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="model-studio-loading">
        <Brain className="w-12 h-12 animate-pulse text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="model-studio">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Model Studio</h2>
          <p className="text-gray-600 mt-1">Train, version, and deploy ML models on new data streams</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowTrainingDialog(true)} data-testid="new-training-btn">
            <Brain className="w-4 h-4 mr-2" />
            New Training Job
          </Button>
          <Button onClick={() => setShowRAGAgentDialog(true)} variant="outline">
            <Bot className="w-4 h-4 mr-2" />
            New RAG Agent
          </Button>
          <Button onClick={() => setShowKnowledgeBaseDialog(true)} variant="outline">
            <Database className="w-4 h-4 mr-2" />
            New Knowledge Base
          </Button>
          <Button onClick={handleCreateMockRAGData} variant="outline">
            <Zap className="w-4 h-4 mr-2" />
            Create Mock RAG Data
          </Button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('models')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'models'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            ML Models
          </button>
          <button
            onClick={() => setActiveTab('rag')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'rag'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            RAG Agents
          </button>
          <button
            onClick={() => setActiveTab('knowledge')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'knowledge'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Knowledge Bases
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'models' && (
        <>
          {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Training Jobs</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{trainingJobs.length}</div>
            <p className="text-xs text-gray-500 mt-1">
              {trainingJobs.filter(j => j.status === 'training').length} active
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Model Versions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{models.length}</div>
            <p className="text-xs text-gray-500 mt-1">
              {models.filter(m => m.is_deployed).length} deployed
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Active Pipelines</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">
              {pipelines.filter(p => p.is_active).length}
            </div>
            <p className="text-xs text-gray-500 mt-1">Out of {pipelines.length}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Avg Performance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">86%</div>
            <p className="text-xs text-gray-500 mt-1">F1-Score</p>
          </CardContent>
        </Card>
      </div>

      {/* Training Jobs */}
      <Card>
        <CardHeader>
          <CardTitle>Training Jobs</CardTitle>
        </CardHeader>
        <CardContent>
          {trainingJobs.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Brain className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No training jobs yet. Create one to get started!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {trainingJobs.map((job) => (
                <div
                  key={job.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                  data-testid={`training-job-${job.id}`}
                >
                  <div className="flex items-center space-x-4 flex-1">
                    {getStatusIcon(job.status)}
                    <div className="flex-1">
                      <div className="font-medium">
                        {job.service_name} / {job.metric_name}
                      </div>
                      <div className="text-sm text-gray-500">
                        Model: {job.model_id}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    {job.status === 'training' && (
                      <div className="text-center">
                        <div className="text-sm font-medium">{job.progress.toFixed(0)}%</div>
                        <div className="text-xs text-gray-500">
                          {job.epochs_completed}/{job.total_epochs} epochs
                        </div>
                      </div>
                    )}
                    
                    {job.validation_metrics?.f1_score && (
                      <div className="text-center">
                        <div className="text-sm font-medium text-green-600">
                          {(job.validation_metrics.f1_score * 100).toFixed(1)}%
                        </div>
                        <div className="text-xs text-gray-500">F1-Score</div>
                      </div>
                    )}

                    <Badge className={getStatusColor(job.status)}>
                      {job.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Model Versions */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Model Versions</CardTitle>
            <Button
              variant="outline"
              onClick={() => {
                if (models.length > 0) {
                  setSelectedModel(models[0]);
                  setDeployForm({ ...deployForm, versionId: models[0].id });
                  setShowDeployDialog(true);
                }
              }}
              disabled={models.length === 0}
            >
              <Rocket className="w-4 h-4 mr-2" />
              Deploy Model
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {models.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <TrendingUp className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No trained models yet. Complete a training job first!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {models.map((model) => (
                <div
                  key={model.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                  data-testid={`model-${model.id}`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium">
                        {model.service_name} / {model.metric_name}
                      </span>
                      <Badge variant="outline">v{model.version}</Badge>
                      <Badge>{model.ensemble_method.toUpperCase()}</Badge>
                      {model.is_deployed && (
                        <Badge className="bg-green-100 text-green-800">
                          <Zap className="w-3 h-3 mr-1" />
                          Deployed
                        </Badge>
                      )}
                    </div>
                    <div className="text-sm text-gray-500">
                      Trained on {model.training_data_points} points • 
                      {new Date(model.trained_at).toLocaleDateString()}
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    {Object.entries(model.training_metrics).slice(0, 3).map(([key, value]) => (
                      <div key={key} className="text-center">
                        <div className="text-sm font-medium">
                          {(value * 100).toFixed(1)}%
                        </div>
                        <div className="text-xs text-gray-500">{key}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Active Pipelines */}
      <Card>
        <CardHeader>
          <CardTitle>Deployed Pipelines</CardTitle>
        </CardHeader>
        <CardContent>
          {pipelines.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Rocket className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No deployed pipelines. Deploy a model to start!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pipelines.map((pipeline) => (
                <div
                  key={pipeline.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                  data-testid={`pipeline-${pipeline.id}`}
                >
                  <div className="flex items-center space-x-4 flex-1">
                    <div
                      className={`w-3 h-3 rounded-full ${
                        pipeline.is_active ? 'bg-green-500 animate-pulse' : 'bg-gray-400'
                      }`}
                    />
                    <div className="flex-1">
                      <div className="font-medium">{pipeline.name}</div>
                      <div className="text-sm text-gray-500">
                        {pipeline.service_name} / {pipeline.metric_name}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <div className="text-sm font-medium">{pipeline.predictions_count}</div>
                      <div className="text-xs text-gray-500">Predictions</div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-medium text-red-600">
                        {pipeline.anomalies_detected}
                      </div>
                      <div className="text-xs text-gray-500">Anomalies</div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-medium text-green-600">
                        {(pipeline.performance_score * 100).toFixed(0)}%
                      </div>
                      <div className="text-xs text-gray-500">Score</div>
                    </div>
                    <Button
                      size="sm"
                      variant={pipeline.is_active ? 'destructive' : 'default'}
                      onClick={() => handleTogglePipeline(pipeline.id, pipeline.is_active)}
                    >
                      {pipeline.is_active ? 'Deactivate' : 'Activate'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Training Dialog */}
      <Dialog open={showTrainingDialog} onOpenChange={setShowTrainingDialog}>
        <DialogContent className="max-w-2xl" data-testid="training-dialog">
          <DialogHeader>
            <DialogTitle>Create Training Job</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium block mb-2">Service</label>
              <Select
                value={trainingForm.serviceName}
                onValueChange={(value) => setTrainingForm({ ...trainingForm, serviceName: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select service" />
                </SelectTrigger>
                <SelectContent>
                  {services.map((service) => (
                    <SelectItem key={service.id} value={service.name}>
                      {service.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium block mb-2">Metric</label>
              <Select
                value={trainingForm.metricName}
                onValueChange={(value) => setTrainingForm({ ...trainingForm, metricName: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {metrics.map((metric) => (
                    <SelectItem key={metric} value={metric}>
                      {metric}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium block mb-2">Ensemble Method</label>
              <Select
                value={trainingForm.ensembleMethod}
                onValueChange={(value) => setTrainingForm({ ...trainingForm, ensembleMethod: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ensembleMethods.map((method) => (
                    <SelectItem key={method.value} value={method.value}>
                      {method.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium block mb-2">Epochs</label>
                <input
                  type="number"
                  className="w-full p-2 border rounded"
                  value={trainingForm.epochs}
                  onChange={(e) => setTrainingForm({ ...trainingForm, epochs: parseInt(e.target.value) })}
                  min="10"
                  max="200"
                />
              </div>
              <div>
                <label className="text-sm font-medium block mb-2">Training Data Points</label>
                <input
                  type="number"
                  className="w-full p-2 border rounded"
                  value={trainingForm.trainingDataPoints}
                  onChange={(e) =>
                    setTrainingForm({ ...trainingForm, trainingDataPoints: parseInt(e.target.value) })
                  }
                  min="100"
                  max="500"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowTrainingDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCreateTrainingJob}
                disabled={!trainingForm.serviceName}
                data-testid="start-training-btn"
              >
                Start Training
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Deploy Dialog */}
      <Dialog open={showDeployDialog} onOpenChange={setShowDeployDialog}>
        <DialogContent data-testid="deploy-dialog">
          <DialogHeader>
            <DialogTitle>Deploy Model</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium block mb-2">Model Version</label>
              <Select
                value={deployForm.versionId}
                onValueChange={(value) => setDeployForm({ ...deployForm, versionId: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select model" />
                </SelectTrigger>
                <SelectContent>
                  {models.map((model) => (
                    <SelectItem key={model.id} value={model.id}>
                      {model.service_name}/{model.metric_name} - v{model.version} ({model.ensemble_method})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium block mb-2">Pipeline Name</label>
              <input
                type="text"
                className="w-full p-2 border rounded"
                placeholder="e.g., production-api-gateway"
                value={deployForm.pipelineName}
                onChange={(e) => setDeployForm({ ...deployForm, pipelineName: e.target.value })}
              />
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowDeployDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleDeployModel}
                disabled={!deployForm.versionId || !deployForm.pipelineName}
                data-testid="deploy-confirm-btn"
              >
                <Rocket className="w-4 h-4 mr-2" />
                Deploy
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* RAG Agent Creation Dialog */}
      <Dialog open={showRAGAgentDialog} onOpenChange={setShowRAGAgentDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create RAG Agent</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-96 overflow-y-auto">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Agent Name</label>
              <input
                type="text"
                value={ragAgentForm.name}
                onChange={(e) => setRAGAgentForm({ ...ragAgentForm, name: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="e.g., Database Incident Specialist"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={ragAgentForm.description}
                onChange={(e) => setRAGAgentForm({ ...ragAgentForm, description: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 h-20"
                placeholder="Describe the agent's expertise and capabilities"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Incident Categories</label>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(INCIDENT_CATEGORIES).map(([key, value]) => (
                  <label key={key} className="flex items-center text-sm">
                    <input
                      type="checkbox"
                      checked={ragAgentForm.categories.includes(value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setRAGAgentForm({
                            ...ragAgentForm,
                            categories: [...ragAgentForm.categories, value]
                          });
                        } else {
                          setRAGAgentForm({
                            ...ragAgentForm,
                            categories: ragAgentForm.categories.filter(cat => cat !== value)
                          });
                        }
                      }}
                      className="mr-2"
                    />
                    {CATEGORY_LABELS[value] || key.replace('_', ' ')}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Knowledge Bases</label>
              <div className="space-y-1">
                {knowledgeBases.map((kb) => (
                  <label key={kb.id} className="flex items-center text-sm">
                    <input
                      type="checkbox"
                      checked={ragAgentForm.knowledge_bases.includes(kb.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setRAGAgentForm({
                            ...ragAgentForm,
                            knowledge_bases: [...ragAgentForm.knowledge_bases, kb.id]
                          });
                        } else {
                          setRAGAgentForm({
                            ...ragAgentForm,
                            knowledge_bases: ragAgentForm.knowledge_bases.filter(id => id !== kb.id)
                          });
                        }
                      }}
                      className="mr-2"
                    />
                    {kb.name} ({CATEGORY_LABELS[kb.category] || kb.category})
                  </label>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Model Provider</label>
                <select
                  value={ragAgentForm.model_provider}
                  onChange={(e) => setRAGAgentForm({ ...ragAgentForm, model_provider: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                >
                  <option value="openai">OpenAI</option>
                  <option value="anthropic">Anthropic</option>
                  <option value="gemini">Gemini</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
                <input
                  type="text"
                  value={ragAgentForm.model_name}
                  onChange={(e) => setRAGAgentForm({ ...ragAgentForm, model_name: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="e.g., gpt-4o"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">System Prompt</label>
              <textarea
                value={ragAgentForm.system_prompt}
                onChange={(e) => setRAGAgentForm({ ...ragAgentForm, system_prompt: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 h-24"
                placeholder="Define the agent's role and behavior"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Tokens</label>
                <input
                  type="number"
                  value={ragAgentForm.max_tokens}
                  onChange={(e) => setRAGAgentForm({ ...ragAgentForm, max_tokens: parseInt(e.target.value) })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Temperature</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="1"
                  value={ragAgentForm.temperature}
                  onChange={(e) => setRAGAgentForm({ ...ragAgentForm, temperature: parseFloat(e.target.value) })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confidence Threshold</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="1"
                  value={ragAgentForm.confidence_threshold}
                  onChange={(e) => setRAGAgentForm({ ...ragAgentForm, confidence_threshold: parseFloat(e.target.value) })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="flex items-center text-sm">
                <input
                  type="checkbox"
                  checked={ragAgentForm.auto_assign}
                  onChange={(e) => setRAGAgentForm({ ...ragAgentForm, auto_assign: e.target.checked })}
                  className="mr-2"
                />
                Auto-assign to new incidents
              </label>

              <label className="flex items-center text-sm">
                <input
                  type="checkbox"
                  checked={ragAgentForm.learning_enabled}
                  onChange={(e) => setRAGAgentForm({ ...ragAgentForm, learning_enabled: e.target.checked })}
                  className="mr-2"
                />
                Enable learning from human feedback
              </label>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowRAGAgentDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateRAGAgent}>
                Create RAG Agent
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Knowledge Base Creation Dialog */}
      <Dialog open={showKnowledgeBaseDialog} onOpenChange={setShowKnowledgeBaseDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Create Knowledge Base</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                value={knowledgeBaseForm.name}
                onChange={(e) => setKnowledgeBaseForm({ ...knowledgeBaseForm, name: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="e.g., Database Troubleshooting SOP"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={knowledgeBaseForm.description}
                onChange={(e) => setKnowledgeBaseForm({ ...knowledgeBaseForm, description: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 h-20"
                placeholder="Describe the knowledge base content and use cases"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={knowledgeBaseForm.category}
                onChange={(e) => setKnowledgeBaseForm({ ...knowledgeBaseForm, category: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="">Select Category</option>
                {Object.entries(INCIDENT_CATEGORIES).map(([key, value]) => (
                  <option key={key} value={value}>
                    {CATEGORY_LABELS[value] || key.replace('_', ' ')}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Content Type</label>
              <select
                value={knowledgeBaseForm.content_type}
                onChange={(e) => setKnowledgeBaseForm({ ...knowledgeBaseForm, content_type: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="structured">Structured Content</option>
                <option value="document">Document Upload</option>
                <option value="sop">Standard Operating Procedure</option>
              </select>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowKnowledgeBaseDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateKnowledgeBase}>
                Create Knowledge Base
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
        </>
      )}

      {/* RAG Agents Tab */}
      {activeTab === 'rag' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Total RAG Agents</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{ragAgents.length}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Active Agents</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{ragAgents.filter(agent => agent.is_active).length}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Auto-Assign Agents</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{ragAgents.filter(agent => agent.auto_assign).length}</div>
              </CardContent>
            </Card>
          </div>

          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">RAG Agents Management</h2>
            <div className="flex gap-2">
              <Button
                onClick={() => setShowExternalRAGStats(true)}
                variant="outline"
                className="flex items-center gap-2"
              >
                <BarChart3 className="h-4 w-4" />
                External Agent Stats
              </Button>
              <Button
                onClick={() => setShowExternalRAGForm(true)}
                className="flex items-center gap-2"
              >
                <Cloud className="h-4 w-4" />
                Create External Agent
              </Button>
            </div>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bot className="h-5 w-5" />
                Internal RAG Agents
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {ragAgents.map((agent) => (
                  <div 
                    key={agent.id} 
                    className="border border-gray-200 rounded-lg p-4 cursor-pointer hover:bg-gray-50 hover:shadow-md transition-all duration-200" 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleViewRAGAgent(agent);
                    }}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">{agent.name}</h3>
                        <p className="text-sm text-gray-600 mt-1">{agent.description}</p>
                        
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          <span>Model: {agent.model_provider}/{agent.model_name}</span>
                          <span>Categories: {agent.categories.length}</span>
                          <span>Knowledge Bases: {agent.knowledge_bases.length}</span>
                          <span>Incidents: {agent.incidents_processed}</span>
                          <span>Success Rate: {Math.round(agent.success_rate)}%</span>
                        </div>

                        <div className="flex gap-2 mt-2">
                          {agent.categories.map((category) => (
                            <Badge key={category} variant="secondary" className="text-xs">
                              {CATEGORY_LABELS[category] || category}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      
                      <div className="ml-4 flex flex-col items-end gap-2">
                        <div className="flex gap-2">
                          <Badge variant={agent.is_active ? "default" : "secondary"}>
                            {agent.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                          {agent.auto_assign && (
                            <Badge variant="outline" className="text-green-600">Auto-Assign</Badge>
                          )}
                        </div>
                        <button 
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleViewRAGAgent(agent);
                          }}
                        >
                          View stats & tune →
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                
                {ragAgents.length === 0 && (
                  <div className="text-center py-12">
                    <Bot className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                    <h3 className="text-sm font-medium text-gray-900 mb-2">No RAG agents</h3>
                    <p className="text-sm text-gray-500">Create your first RAG agent to start automated incident analysis.</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Knowledge Bases Tab */}
      {activeTab === 'knowledge' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Knowledge Bases</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{knowledgeBases.length}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Categories Covered</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">
                  {new Set(knowledgeBases.map(kb => kb.category)).size}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Active Knowledge Bases</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{knowledgeBases.filter(kb => kb.is_active).length}</div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Knowledge Bases</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {knowledgeBases.map((kb) => (
                  <div 
                    key={kb.id} 
                    className="border border-gray-200 rounded-lg p-4 cursor-pointer hover:bg-gray-50 hover:shadow-md transition-all duration-200" 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleViewKnowledgeBase(kb);
                    }}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">{kb.name}</h3>
                        <p className="text-sm text-gray-600 mt-1">{kb.description}</p>
                        
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          <span>Type: {kb.content_type}</span>
                          <span>Category: {CATEGORY_LABELS[kb.category] || kb.category}</span>
                          <span>Used: {kb.usage_count} times</span>
                          <span>Version: {kb.version}</span>
                        </div>

                        <div className="flex gap-1 mt-2">
                          {kb.tags.map((tag, index) => (
                            <Badge key={index} variant="outline" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      
                      <div className="ml-4 flex flex-col gap-2">
                        <Badge variant={kb.is_active ? "default" : "secondary"}>
                          {kb.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                        <div className="flex flex-col gap-1">
                          <Button 
                            size="sm"
                            variant="outline"
                            className="text-xs h-8"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleViewKnowledgeBase(kb);
                            }}
                          >
                            <FileText className="mr-1 h-3 w-3" />
                            Manage Docs
                          </Button>
                          <Button 
                            size="sm"
                            className="text-xs h-8"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setSelectedKnowledgeBase(kb);
                              setShowDocumentManager(true);
                            }}
                          >
                            <Upload className="mr-1 h-3 w-3" />
                            Upload
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                
                {knowledgeBases.length === 0 && (
                  <div className="text-center py-12">
                    <Database className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                    <h3 className="text-sm font-medium text-gray-900 mb-2">No knowledge bases</h3>
                    <p className="text-sm text-gray-500">Create knowledge bases to provide context for RAG agents.</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Knowledge Base Detail Modal */}
      {showKnowledgeBaseDetail && selectedKnowledgeBase && (
        <Dialog open={showKnowledgeBaseDetail} onOpenChange={setShowKnowledgeBaseDetail}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedKnowledgeBase.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-6">
              {/* Knowledge Base Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <h4 className="font-medium text-gray-900">Category</h4>
                  <p className="text-sm text-gray-600">
                    {CATEGORY_LABELS[selectedKnowledgeBase.category] || selectedKnowledgeBase.category || 'Not specified'}
                  </p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Content Type</h4>
                  <p className="text-sm text-gray-600">{selectedKnowledgeBase.content_type || 'Unknown'}</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Usage Count</h4>
                  <p className="text-sm text-gray-600">{selectedKnowledgeBase.usage_count || 0} times</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Version</h4>
                  <p className="text-sm text-gray-600">v{selectedKnowledgeBase.version || 1}</p>
                </div>
              </div>

              {/* API Status Indicator */}
              {!selectedKnowledgeBase.structured_content && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm text-yellow-800">
                    📡 Loading detailed information... (Showing basic info from cache)
                  </p>
                </div>
              )}

              {/* Description */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <h4 className="font-medium text-gray-900">Description</h4>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditingKB(!editingKB)}
                  >
                    {editingKB ? 'Cancel' : 'Edit Details'}
                  </Button>
                </div>
                {editingKB ? (
                  <div className="space-y-3">
                    <textarea
                      value={selectedKnowledgeBase.description || ''}
                      onChange={(e) => setSelectedKnowledgeBase({
                        ...selectedKnowledgeBase,
                        description: e.target.value
                      })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 h-20"
                      placeholder="Update knowledge base description..."
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => {
                          handleUpdateKnowledgeBase({
                            description: selectedKnowledgeBase.description
                          });
                          setEditingKB(false);
                        }}
                      >
                        Save Changes
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-600">{selectedKnowledgeBase.description}</p>
                )}
              </div>

              {/* Structured Content - Procedures */}
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h4 className="font-medium text-gray-900">
                    Procedures ({(selectedKnowledgeBase.structured_content?.procedures || []).length})
                  </h4>
                  <Button onClick={() => setShowAddProcedure(true)} size="sm">
                    Add Procedure
                  </Button>
                </div>
                {selectedKnowledgeBase.structured_content?.procedures && selectedKnowledgeBase.structured_content.procedures.length > 0 ? (
                  <div className="space-y-4">
                    {selectedKnowledgeBase.structured_content.procedures.map((procedure, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4">
                        <h5 className="font-medium text-gray-900">Step {procedure.step}: {procedure.title}</h5>
                        {procedure.actions && procedure.actions.length > 0 ? (
                          <ul className="mt-2 space-y-1">
                            {procedure.actions.map((action, actionIndex) => (
                              <li key={actionIndex} className="text-sm text-gray-600 ml-4">
                                • {action}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-sm text-gray-500 mt-2">No actions defined for this procedure</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <p className="text-gray-500">No procedures defined yet</p>
                    <Button onClick={() => setShowAddProcedure(true)} size="sm" className="mt-2">
                      Add First Procedure
                    </Button>
                  </div>
                )}
              </div>

              {/* Tags */}
              {selectedKnowledgeBase.tags && selectedKnowledgeBase.tags.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Tags</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedKnowledgeBase.tags.map((tag, index) => (
                      <Badge key={index} variant="outline">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Add Procedure Modal */}
      {showAddProcedure && (
        <Dialog open={showAddProcedure} onOpenChange={setShowAddProcedure}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Add New Procedure</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Step Number</label>
                <input
                  type="number"
                  value={newProcedure.step}
                  onChange={(e) => setNewProcedure({ ...newProcedure, step: parseInt(e.target.value) })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Procedure Title</label>
                <input
                  type="text"
                  value={newProcedure.title}
                  onChange={(e) => setNewProcedure({ ...newProcedure, title: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="e.g., Initial System Check"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Actions</label>
                {newProcedure.actions.map((action, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={action}
                      onChange={(e) => {
                        const newActions = [...newProcedure.actions];
                        newActions[index] = e.target.value;
                        setNewProcedure({ ...newProcedure, actions: newActions });
                      }}
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2"
                      placeholder={`Action ${index + 1}`}
                    />
                    {newProcedure.actions.length > 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const newActions = newProcedure.actions.filter((_, i) => i !== index);
                          setNewProcedure({ ...newProcedure, actions: newActions });
                        }}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setNewProcedure({ 
                    ...newProcedure, 
                    actions: [...newProcedure.actions, ''] 
                  })}
                >
                  Add Action
                </Button>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <Button variant="outline" onClick={() => setShowAddProcedure(false)}>
                  Cancel
                </Button>
                <Button onClick={handleAddProcedure}>
                  Add Procedure
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* RAG Agent Detail Modal */}
      {showRAGAgentDetail && selectedRAGAgent && (
        <Dialog open={showRAGAgentDetail} onOpenChange={setShowRAGAgentDetail}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>RAG Agent: {selectedRAGAgent.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-6">
              {/* Agent Statistics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-medium text-blue-900">Incidents Processed</h4>
                  <p className="text-2xl font-bold text-blue-700">{agentStats.incidents_processed || 0}</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="font-medium text-green-900">Success Rate</h4>
                  <p className="text-2xl font-bold text-green-700">{agentStats.success_rate || 0}%</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <h4 className="font-medium text-purple-900">Recommendations</h4>
                  <p className="text-2xl font-bold text-purple-700">{agentStats.total_recommendations || 0}</p>
                </div>
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <h4 className="font-medium text-yellow-900">Learning Events</h4>
                  <p className="text-2xl font-bold text-yellow-700">{agentStats.learning_events || 0}</p>
                </div>
              </div>

              {/* Agent Configuration */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <h4 className="font-medium text-gray-900">Model</h4>
                  <p className="text-sm text-gray-600">{selectedRAGAgent.model_provider}/{selectedRAGAgent.model_name}</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Temperature</h4>
                  <p className="text-sm text-gray-600">{selectedRAGAgent.temperature}</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Max Tokens</h4>
                  <p className="text-sm text-gray-600">{selectedRAGAgent.max_tokens}</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Confidence Threshold</h4>
                  <p className="text-sm text-gray-600">{selectedRAGAgent.confidence_threshold}</p>
                </div>
              </div>

              {/* Categories */}
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Incident Categories ({selectedRAGAgent.categories.length})</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedRAGAgent.categories.map((category) => (
                    <Badge key={category} variant="secondary">
                      {CATEGORY_LABELS[category] || category}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* System Prompt */}
              <div>
                <h4 className="font-medium text-gray-900 mb-2">System Prompt</h4>
                <textarea
                  value={selectedRAGAgent.system_prompt}
                  onChange={(e) => setSelectedRAGAgent({
                    ...selectedRAGAgent,
                    system_prompt: e.target.value
                  })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 h-32"
                />
              </div>

              {/* Fine-tuning Controls */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-medium text-gray-900">Fine-tuning Controls</h4>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditingAgent(!editingAgent)}
                  >
                    {editingAgent ? 'Cancel Editing' : 'Enable Editing'}
                  </Button>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Temperature</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="1"
                      value={selectedRAGAgent.temperature}
                      onChange={(e) => setSelectedRAGAgent({
                        ...selectedRAGAgent,
                        temperature: parseFloat(e.target.value)
                      })}
                      disabled={!editingAgent}
                      className={`w-full border border-gray-300 rounded-md px-3 py-2 ${!editingAgent ? 'bg-gray-100' : ''}`}
                    />
                    <p className="text-xs text-gray-500 mt-1">Controls randomness (0-1)</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Tokens</label>
                    <input
                      type="number"
                      min="100"
                      max="4000"
                      value={selectedRAGAgent.max_tokens}
                      onChange={(e) => setSelectedRAGAgent({
                        ...selectedRAGAgent,
                        max_tokens: parseInt(e.target.value)
                      })}
                      disabled={!editingAgent}
                      className={`w-full border border-gray-300 rounded-md px-3 py-2 ${!editingAgent ? 'bg-gray-100' : ''}`}
                    />
                    <p className="text-xs text-gray-500 mt-1">Max response length</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Confidence Threshold</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      max="1"
                      value={selectedRAGAgent.confidence_threshold}
                      onChange={(e) => setSelectedRAGAgent({
                        ...selectedRAGAgent,
                        confidence_threshold: parseFloat(e.target.value)
                      })}
                      disabled={!editingAgent}
                      className={`w-full border border-gray-300 rounded-md px-3 py-2 ${!editingAgent ? 'bg-gray-100' : ''}`}
                    />
                    <p className="text-xs text-gray-500 mt-1">Min confidence to act</p>
                  </div>
                </div>

                {/* Model Selection for Testing Different LLMs */}
                <div className="mt-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                  <h5 className="font-medium text-gray-900 mb-2">Test Different LLM Models</h5>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Model Provider</label>
                      <select
                        value={selectedRAGAgent.model_provider}
                        onChange={(e) => setSelectedRAGAgent({
                          ...selectedRAGAgent,
                          model_provider: e.target.value
                        })}
                        disabled={!editingAgent}
                        className={`w-full border border-gray-300 rounded-md px-3 py-2 ${!editingAgent ? 'bg-gray-100' : ''}`}
                      >
                        <option value="openai">OpenAI</option>
                        <option value="anthropic">Anthropic</option>
                        <option value="gemini">Gemini</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Model Name</label>
                      <select
                        value={selectedRAGAgent.model_name}
                        onChange={(e) => setSelectedRAGAgent({
                          ...selectedRAGAgent,
                          model_name: e.target.value
                        })}
                        disabled={!editingAgent}
                        className={`w-full border border-gray-300 rounded-md px-3 py-2 ${!editingAgent ? 'bg-gray-100' : ''}`}
                      >
                        {selectedRAGAgent.model_provider === 'openai' && (
                          <>
                            <option value="gpt-4o">GPT-4o (Latest)</option>
                            <option value="gpt-4o-mini">GPT-4o Mini</option>
                            <option value="gpt-4-turbo">GPT-4 Turbo</option>
                          </>
                        )}
                        {selectedRAGAgent.model_provider === 'anthropic' && (
                          <>
                            <option value="claude-3-5-sonnet-20241022">Claude 3.5 Sonnet (Latest)</option>
                            <option value="claude-3-haiku-20240307">Claude 3 Haiku</option>
                          </>
                        )}
                        {selectedRAGAgent.model_provider === 'gemini' && (
                          <>
                            <option value="gemini-1.5-pro-002">Gemini 1.5 Pro</option>
                            <option value="gemini-1.5-flash-002">Gemini 1.5 Flash</option>
                          </>
                        )}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* LLM Testing Section */}
              <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
                <h4 className="font-medium text-blue-900 mb-3">🧪 Test LLM Response</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-blue-800 mb-1">Test Prompt</label>
                    <textarea
                      value={testPrompt}
                      onChange={(e) => setTestPrompt(e.target.value)}
                      placeholder="Enter a test prompt to see how the current model configuration responds..."
                      className="w-full border border-blue-300 rounded-md px-3 py-2 h-20"
                    />
                  </div>
                  <Button
                    onClick={handleTestLLM}
                    disabled={!testPrompt.trim() || testingLLM}
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {testingLLM ? 'Testing...' : '🚀 Test Current Configuration'}
                  </Button>
                  
                  {testResult && (
                    <div className="mt-3 p-3 bg-white border border-blue-200 rounded-md">
                      <h5 className="font-medium text-blue-900 mb-2">Test Result:</h5>
                      <div className="text-sm text-gray-700 whitespace-pre-wrap">{testResult}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Recent Learning History */}
              {selectedRAGAgent.recent_learning && selectedRAGAgent.recent_learning.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Recent Learning Events</h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {selectedRAGAgent.recent_learning.map((event, index) => (
                      <div key={index} className="border border-gray-200 rounded p-2 text-sm">
                        <div className="flex justify-between">
                          <span className={`font-medium ${event.action_taken === 'validated' ? 'text-green-600' : 'text-red-600'}`}>
                            {event.action_taken.toUpperCase()}
                          </span>
                          <span className="text-gray-500">{new Date(event.timestamp).toLocaleString()}</span>
                        </div>
                        <p className="text-gray-600">{event.human_feedback}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-between">
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setTestPrompt('');
                      setTestResult('');
                    }}
                    size="sm"
                  >
                    Clear Test
                  </Button>
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => {
                    setShowRAGAgentDetail(false);
                    setEditingAgent(false);
                    setTestPrompt('');
                    setTestResult('');
                  }}>
                    Close
                  </Button>
                  <Button 
                    onClick={() => {
                      handleUpdateRAGAgent({
                        system_prompt: selectedRAGAgent.system_prompt,
                        temperature: selectedRAGAgent.temperature,
                        max_tokens: selectedRAGAgent.max_tokens,
                        confidence_threshold: selectedRAGAgent.confidence_threshold,
                        model_provider: selectedRAGAgent.model_provider,
                        model_name: selectedRAGAgent.model_name
                      });
                      setEditingAgent(false);
                    }}
                    disabled={!editingAgent}
                  >
                    💾 Save All Changes
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Document Manager Dialog */}
      <Dialog open={showDocumentManager} onOpenChange={setShowDocumentManager}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              Document Management - {selectedKnowledgeBase?.name}
            </DialogTitle>
          </DialogHeader>
          {selectedKnowledgeBase && (
            <DocumentManager
              knowledgeBaseId={selectedKnowledgeBase.id}
              knowledgeBaseName={selectedKnowledgeBase.name}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* External RAG Agent Form Dialog */}
      <Dialog open={showExternalRAGForm} onOpenChange={setShowExternalRAGForm}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cloud className="h-5 w-5" />
              Create External RAG Agent
            </DialogTitle>
          </DialogHeader>
          <ExternalRAGAgentForm
            onSubmit={handleCreateExternalRAGAgent}
            onCancel={() => setShowExternalRAGForm(false)}
            knowledgeBases={knowledgeBases}
            categories={Object.keys(CATEGORY_LABELS)}
          />
        </DialogContent>
      </Dialog>

      {/* External RAG Agent Stats Dialog */}
      <Dialog open={showExternalRAGStats} onOpenChange={setShowExternalRAGStats}>
        <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              External RAG Agents Statistics
            </DialogTitle>
          </DialogHeader>
          <ExternalRAGStats />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ModelStudio;